import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { PromoStateComparisonChart } from '@/components/spar-mobile/PromoStateComparisonChart';
import {
  StateComparisonRow,
  UpliftRow,
  ANALYSIS_START_DATE,
  analysisEndDate,
} from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';
import { exportElementToPdf } from '@/utils/exportPdf';

function formatLongDate(iso: string): string {
  return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function daysBetween(startIso: string, endIso: string): number {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

export function PromoAttributionExecutiveSummaryPage() {
  const [stateData, setStateData] = useState<StateComparisonRow[]>([]);
  const [upliftData, setUpliftData] = useState<UpliftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const startDate = ANALYSIS_START_DATE;
  const endDate = analysisEndDate();
  const windowDays = daysBetween(startDate, endDate);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stateRes, upliftRes] = await Promise.all([
        api.get('/spar-mobile/promo-attribution/state-comparison'),
        api.get('/spar-mobile/promo-attribution/top-uplift?limit=5'),
      ]);
      setStateData(stateRes.data.data ?? []);
      setUpliftData(upliftRes.data.data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportPdf = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await exportElementToPdf(printRef.current, {
        filename: `promo-attribution-summary-${date}.pdf`,
      });
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Could not generate PDF', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setExporting(false);
    }
  };

  const baseline = stateData.find((r) => r.state === 'Standard Price');
  const combined = stateData.find((r) => r.state === 'Promotion and Promo Data');
  const promotion = stateData.find((r) => r.state === 'Promotion');

  // Headline: how much better did Promotion + Promo Data days perform vs the
  // product's normal (Standard Price) baseline. This is the single number a
  // commercial stakeholder asks: "did the activations actually move volume?"
  const headlineUplift =
    baseline && combined && baseline.avg_daily_units > 0
      ? ((combined.avg_daily_units - baseline.avg_daily_units) /
          baseline.avg_daily_units) *
        100
      : null;

  // Same comparison vs a "promotion only" (discount, no promo data) day, to
  // separate the contribution of the promo-data layer from the discount layer.
  const liftVsPromotion =
    promotion && combined && promotion.avg_daily_units > 0
      ? ((combined.avg_daily_units - promotion.avg_daily_units) /
          promotion.avg_daily_units) *
        100
      : null;

  const productsWithPromoData = combined?.distinct_products ?? 0;
  const promoDataDays = combined?.distinct_calendar_days ?? 0;
  const topMover = upliftData[0] ?? null;

  // Top-5 spread, used to give the table a live interpretive caption.
  const topFive = upliftData.slice(0, 5);
  const topFiveMin =
    topFive.length > 0 ? Math.min(...topFive.map((r) => r.uplift_pct)) : null;
  const topFiveMax =
    topFive.length > 0 ? Math.max(...topFive.map((r) => r.uplift_pct)) : null;

  const headlineSentence = (() => {
    if (loading) return 'Loading the latest figures…';
    if (headlineUplift == null) {
      return 'Not enough comparable days yet to compute a headline figure.';
    }
    const dir = headlineUplift >= 0 ? 'sold more' : 'sold fewer';
    const abs = Math.abs(headlineUplift);
    return (
      `On days when a SPAR Mobile promo-data activation ran on a product, ` +
      `that product ${dir} units — about ` +
      `${formatPromoNumber(abs, 1)}% ${headlineUplift >= 0 ? 'more' : 'less'} ` +
      `per day — than on the same product's standard-price days.`
    );
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 justify-end">
        <button
          onClick={handleExportPdf}
          disabled={exporting || stateData.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting
            ? <IconLoader2 size={14} className="animate-spin" />
            : <IconDownload size={14} />}
          {exporting ? 'Generating…' : 'Export PDF'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div ref={printRef} className="space-y-6 bg-background">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            SPAR Mobile · Executive Summary
          </p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">
            Did promo data drive incremental sales?
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Analysis window: <strong>{formatLongDate(startDate)}</strong>
            {' '}→{' '}<strong>{formatLongDate(endDate)}</strong>
            {' '}({formatPromoNumber(windowDays)} days)
          </p>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            The headline
          </p>
          <p className="text-xl leading-relaxed mt-2">{headlineSentence}</p>
          {liftVsPromotion != null && (
            <p className="text-sm text-muted-foreground mt-3">
              Compared to days where a normal price discount ran without a
              promo-data activation, those products still sold{' '}
              <strong className={cn(liftVsPromotion >= 0 ? 'text-emerald-700' : 'text-red-600')}>
                {liftVsPromotion >= 0 ? '+' : ''}{formatPromoNumber(liftVsPromotion, 1)}%
              </strong>
              {' '}more — suggesting the promo-data layer adds lift on top of the
              discount itself.
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-3">
            In short: a price discount on its own brings some lift, but the
            biggest unit gains land on days where SPAR Mobile activations and a
            discount run together.
          </p>
        </div>

        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
        >
          <KpiCard
            label="Lift vs standard"
            value={
              headlineUplift != null
                ? `${headlineUplift >= 0 ? '+' : ''}${formatPromoNumber(headlineUplift, 1)}%`
                : '—'
            }
            tone={headlineUplift != null && headlineUplift >= 0 ? 'positive' : 'neutral'}
            footnote="Promotion + Promo Data days vs Standard Price days"
          />
          <KpiCard
            label="Products with activations"
            value={formatPromoNumber(productsWithPromoData)}
            footnote={`${formatPromoNumber(promoDataDays)} promo-data days`}
          />
          <KpiCard
            label="Top performer"
            value={
              topMover
                ? `+${formatPromoNumber(topMover.uplift_pct, 1)}%`
                : '—'
            }
            tone="positive"
            footnote={
              topMover
                ? topMover.description ?? `GTIN ${topMover.gtin}`
                : 'No products meet threshold yet'
            }
          />
          <KpiCard
            label="Analysis window"
            value={`${formatPromoNumber(windowDays)} days`}
            footnote={`${formatLongDate(startDate)} → ${formatLongDate(endDate)}`}
          />
        </div>

        <div className="rounded-lg border bg-card">
          <div className="p-4 pb-2">
            <p className="text-base font-semibold">How each day-type performs</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Average units sold per product per day, grouped by what was
              happening that day. Higher is better.
            </p>
          </div>
          <div className="p-4 pt-0">
            {stateData.length > 0 ? (
              <PromoStateComparisonChart data={stateData} metric="avg_daily_units" />
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                {loading ? 'Loading…' : 'No data available.'}
              </div>
            )}
          </div>
          <div className="border-t bg-muted/20 px-4 py-3 text-sm leading-relaxed">
            <strong>Reading the chart.</strong> Standard Price is the baseline —
            what a typical day looks like for these products. Promotion days
            (price discount, no promo-data activation) sit modestly above.
            Promotion + Promo Data days are the highest: the gap between those
            and Promotion is the marginal contribution of the promo-data layer
            on top of the discount itself.
          </div>
        </div>

        {upliftData.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="p-4 pb-2">
              <p className="text-base font-semibold">Top 5 products by uplift</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Products where promo-data days clearly outperformed the
                product's own standard-price days.
              </p>
            </div>
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Product</th>
                    <th className="px-4 py-2 font-medium text-right">Standard avg / day</th>
                    <th className="px-4 py-2 font-medium text-right">Promo data avg / day</th>
                    <th className="px-4 py-2 font-medium text-right">Uplift</th>
                  </tr>
                </thead>
                <tbody>
                  {upliftData.slice(0, 5).map((row) => (
                    <tr key={row.gtin} className="border-t">
                      <td className="px-4 py-2">
                        <Link
                          to={`/spar-mobile/promo-attribution/products/${row.gtin}`}
                          className="hover:underline"
                        >
                          <div className="font-medium">{row.description ?? `GTIN ${row.gtin}`}</div>
                          {row.description && (
                            <div className="text-xs text-muted-foreground font-mono">{row.gtin}</div>
                          )}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                        {formatPromoNumber(row.avg_units_no_promo_data, 1)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatPromoNumber(row.avg_units_promo_data, 1)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2 text-right tabular-nums font-semibold',
                          row.uplift_pct >= 0 ? 'text-emerald-700' : 'text-red-600',
                        )}
                      >
                        {row.uplift_pct >= 0 ? '+' : ''}
                        {formatPromoNumber(row.uplift_pct, 1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {topFiveMin != null && topFiveMax != null && (
              <div className="border-t bg-muted/20 px-4 py-3 text-sm leading-relaxed">
                <strong>Reading the table.</strong> The top five sit between{' '}
                <strong>+{formatPromoNumber(topFiveMin, 1)}%</strong> and{' '}
                <strong>+{formatPromoNumber(topFiveMax, 1)}%</strong> on
                activation days. Lower-baseline products tend to rank highly
                because small absolute changes look large in percentage terms —
                when prioritising follow-up, weight by absolute units sold, not
                just the headline %.
              </div>
            )}
          </div>
        )}

        {/*
          Data-quality section. Numbers are a snapshot from the May data-quality
          investigation (see canvases/spar-basket-price-ambiguity.canvas.tsx).
          Framed for a commercial audience as the validation work that gives
          confidence in the unit numbers above, rather than as a problem report
          on existing code.
        */}
        <div className="rounded-lg border bg-card">
          <div className="p-5 pb-3 border-b">
            <p className="text-base font-semibold">
              How we validated the source data
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              The reconciliation work that sits behind every figure on this page.
            </p>
          </div>
          <div className="p-5 space-y-4 text-sm">
            <p>
              Before publishing this analysis, we ran a thorough reconciliation
              of SPAR's basket feed against the basket totals SPAR sends —{' '}
              <strong>~1.5 million line items</strong> across{' '}
              <strong>~257,000 baskets</strong> over a four-day sample. The
              exercise tells us which line-level signals are trustworthy today
              and which need additional work before we put numbers next to them.
            </p>
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
            >
              <SmallStat
                label="Line items reviewed"
                value="~1.5M"
                tone="neutral"
              />
              <SmallStat
                label="Baskets reviewed"
                value="~257K"
                tone="neutral"
              />
              <SmallStat
                label="Price-field patterns mapped"
                value="4"
                tone="positive"
              />
            </div>
            <p>
              SPAR's basket feed uses{' '}
              <strong>four distinct patterns</strong> for the line-level{' '}
              <code className="text-xs bg-muted px-1 rounded">price</code>{' '}
              field, each driven by a different scanning path or product type.
              Mapping them is what lets us aggregate the data confidently:
            </p>
            <ul className="space-y-1.5 list-disc pl-5">
              <li>
                Per-unit price with a quantity of 1 — the default supermarket
                barcode scan.
              </li>
              <li>
                Per-unit price with quantity greater than 1 — e.g. 3 × Monster
                cans rung up together.
              </li>
              <li>
                Case-barcode scans where the line price represents the full
                case — e.g. a 12-pack of Heineken.
              </li>
              <li>
                Weighed / loose items (produce, deli, "WATER 2 GO PER LITRE")
                where the line price already carries the total for what was
                weighed.
              </li>
            </ul>
            <p>
              Mapping these patterns gives us a clean basis to resolve every
              line correctly — including baskets that combine more than one
              pattern in a single transaction. A per-line resolver applies the
              right rule for each line.
            </p>
            <div className="rounded-md border-l-4 border-blue-500 bg-blue-50/40 p-3 text-sm">
              <strong>Why this matters for the figures above.</strong> Unit
              comparisons are reliable across all four patterns, so the lift
              numbers on this page can be acted on with confidence. Rand
              revenue is the next milestone — the resolver work above is what
              unlocks publishing it next to the unit lift.
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <div className="p-5 pb-3 border-b">
            <p className="text-base font-semibold">What we'd watch next</p>
            <p className="text-sm text-muted-foreground mt-1">
              Three follow-ups that would deepen this picture in the next cycle.
            </p>
          </div>
          <div className="p-5 space-y-3">
            <NextItem
              title="Revenue alongside units"
              desc="Once the per-line resolver lands, we can publish rand impact next to the unit lift you see here — turning &ldquo;sold X% more&rdquo; into &ldquo;earned R X more&rdquo;."
            />
            <NextItem
              title="Mix vs new demand"
              desc="A unit lift can come from existing customers buying more, new customers being acquired, or the same customers shifting timing. Customer-level analysis can separate these — and shapes how we value future activations."
            />
            <NextItem
              title="Promo Data on its own"
              desc="Today, days where promo data ran without a price discount are too rare to compare meaningfully. As that bucket grows we can measure whether the activation alone — without a discount — moves units."
            />
          </div>
        </div>

        <div className="rounded-lg border bg-muted/20 p-5 text-xs text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground">Method &amp; caveats</p>
          <p>
            All comparisons are <strong>unit-based</strong>. A product is
            included only when it has at least three days in both promo-data
            and standard-price buckets, which keeps single-day flukes out of
            the headline. Promo-data-only days (no associated price discount)
            are excluded from the headline because the sample is too thin to
            interpret today.
          </p>
          <p>
            For the full method —{' '}
            <Link
              to="/spar-mobile/promo-attribution/methodology"
              className="text-primary underline underline-offset-2"
            >
              see the methodology page
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

interface SmallStatProps {
  label: string;
  value: string;
  tone: 'positive' | 'negative' | 'neutral';
}

function SmallStat({ label, value, tone }: SmallStatProps) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2',
        tone === 'positive' && 'border-emerald-200 bg-emerald-50/40',
        tone === 'negative' && 'border-red-200 bg-red-50/40',
        tone === 'neutral' && 'border-input bg-muted/20',
      )}
    >
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p
        className={cn(
          'text-xl font-bold tabular-nums leading-tight',
          tone === 'positive' && 'text-emerald-700',
          tone === 'negative' && 'text-red-700',
        )}
      >
        {value}
      </p>
    </div>
  );
}

interface NextItemProps {
  title: string;
  desc: string;
}

function NextItem({ title, desc }: NextItemProps) {
  return (
    <div className="flex gap-3">
      <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
      <div className="text-sm space-y-0.5">
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  footnote?: string;
  tone?: 'positive' | 'neutral';
}

function KpiCard({ label, value, footnote, tone = 'neutral' }: KpiCardProps) {
  return (
    <div className="rounded-lg border bg-card p-5 space-y-1">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p
        className={cn(
          'text-3xl font-bold tabular-nums',
          tone === 'positive' && 'text-emerald-700',
        )}
      >
        {value}
      </p>
      {footnote && (
        <p className="text-xs text-muted-foreground">{footnote}</p>
      )}
    </div>
  );
}
